import { useEffect, useState } from "react";
import dayjs from "dayjs";
import AtriumApi from "api";
import Card, { CardHeader } from "components/card";
import { Select } from "components/fields";
import Loader from "components/loader/Loader";
import { formatTime, formatWeekday } from "utils/format";

const Weather = () => {
  const [forecastData, setForecastData] = useState([]);
  const [church, setChurch] = useState();

  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCampus, setFilterCampus] = useState("all");

  useEffect(() => {
    const fetchAllCampuses = async () => {
      try {
        const response = await AtriumApi.get("/v2/campuses/allCampuses");
        const campusesData = response?.data?.data?.campuses?.data || [];
        if (campusesData.length > 0) {
          setCampuses(campusesData);
          setFilterCampus(campusesData[0]);
        }
      } catch (error) {
        console.log("fetching all campuses error", error);
      }
    };
    fetchAllCampuses();
  }, []);

  useEffect(() => {
    const fetchChurch = async () => {
      try {
        const response = await AtriumApi.get("/v1/church");
        if (response) {
          setChurch(response?.data?.church);
        }
      } catch (error) {
        console.log("error fetching church", error);
      }
    };

    fetchChurch();
  }, []);

  useEffect(() => {
    if (!church) return;

    function buildWeatherQueryString() {
      let coordinates = null;

      // A selected campus uses its own location.coordinates.
      if (filterCampus && filterCampus !== "all") {
        coordinates = filterCampus.location?.coordinates;
      }

      // Fall back to the church coordinates when the campus has none.
      if (!coordinates && church?.location?.coordinates) {
        coordinates = church.location.coordinates;
      }

      if (coordinates && Array.isArray(coordinates) && coordinates.length >= 2) {
        const [lon, lat] = coordinates;
        return `lat=${lat}&lon=${lon}`;
      }

      console.warn("No valid coordinates found for weather");
      return "";
    }

    const fetchWeather = async () => {
      try {
        setLoading(true);
        const query = buildWeatherQueryString();

        if (!query) {
          console.error("No coordinates available for weather");
          setLoading(false);
          return;
        }

        const response = await AtriumApi.get(
          `/v1/weather${query ? `?${query}` : ""}`
        );
        if (response) {
          setLoading(false);
          setForecastData(response?.data);
        }
      } catch (error) {
        setLoading(false);
        console.error("Failed to fetch weather data:", error);
      }
    };

    fetchWeather();
  }, [church, filterCampus]);

  if (!forecastData) return null;

  const campusOptions =
    campuses?.length > 0
      ? campuses.map((campus) => ({ value: campus?.id, label: campus?.name }))
      : [{ value: "", label: "No campus found", disabled: true }];

  const today = dayjs();
  const forecastDays = (forecastData?.forecast?.forecastday || [])
    // Only today and future days, up to a week.
    .filter((dayData) => !dayjs(dayData?.date).isBefore(today, "day"))
    .slice(0, 7);

  return (
    <Card padding="md" className="h-full gap-4">
      <CardHeader
        title="Weather"
        actions={
          <Select
            aria-label="Campus"
            className="w-44"
            value={filterCampus === "all" ? "all" : filterCampus?.id}
            onChange={(e) => {
              const selectedId = Number(e.target.value);
              const selectedCampus = campuses?.find((c) => c?.id === selectedId);
              setFilterCampus(selectedCampus);
            }}
            options={campusOptions}
          />
        }
      />

      {loading ? (
        <Loader label="Loading weather" />
      ) : (
        <>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                className="h-14 w-14 md:h-16 md:w-16"
                src={forecastData?.current?.condition?.icon}
                alt={forecastData?.current?.condition?.text}
              />
              <p className="text-4xl font-bold text-fg md:text-5xl">
                {parseInt(forecastData?.current?.temp_f) || ""}°
              </p>
            </div>

            <div className="text-right">
              <p className="text-sm font-medium text-fg">
                {forecastData?.current?.condition?.text}
              </p>
              <p className="mt-1 text-xs text-fg-muted">{formatTime(new Date())}</p>
            </div>
          </div>

          <div className="border-t border-line pt-3">
            <div className="hide-scrollbar flex items-center justify-between gap-2 overflow-x-auto pb-1">
              {forecastDays.map((dayData, i) => {
                const forecastDate = dayjs(dayData?.date);
                const dayLabel = forecastDate.isSame(today, "day")
                  ? "Today"
                  : formatWeekday(forecastDate);

                return (
                  <div
                    className="flex min-w-14 flex-shrink-0 flex-col items-center px-1 text-center"
                    key={`weather${i}`}
                  >
                    <p className="mb-0.5 text-2xs font-semibold uppercase text-fg-muted">
                      {dayLabel}
                    </p>
                    <img
                      className="my-0.5 h-8 w-8 object-contain"
                      src={dayData?.day?.condition?.icon}
                      alt={dayData?.day?.condition?.text}
                    />
                    <p className="text-xs font-bold text-fg">
                      {parseInt(dayData?.day?.avgtemp_f) || ""}°
                    </p>
                    <p className="text-2xs text-fg-muted">
                      {parseInt(dayData?.day?.maxtemp_f)}°/{parseInt(dayData?.day?.mintemp_f)}°
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default Weather;
